import React, { useEffect, useState, useCallback } from 'react';
import { DashboardLayout } from '../components/layout/DashboardLayout';
import { useI18n } from '../contexts/I18nContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Switch } from '../components/ui/switch';
import { Badge } from '../components/ui/badge';
import { getAlertConfigs, updateAlertConfig, getAlertLogs, testAlert, checkAlertsNow } from '../lib/api';
import { toast } from 'sonner';
import { Bell, Anchor, Clock, Plus, Trash2, Send, Play, Loader2, Mail, CheckCircle, AlertTriangle, ShieldCheck, Ship, FileBarChart } from 'lucide-react';

const ALERT_ICONS = {
  port_days: Anchor,
  arrival_days: Clock,
  cert_expiring: ShieldCheck,
  port_arrival: Ship,
  monthly_report: FileBarChart,
};

const ALERT_COLORS = {
  port_days: 'text-amber-500',
  arrival_days: 'text-red-500',
  cert_expiring: 'text-purple-500',
  port_arrival: 'text-green-500',
  monthly_report: 'text-blue-500',
};

export default function AlertsPage() {
  const { t } = useI18n();
  const [configs, setConfigs] = useState([]);
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState({});
  const [testing, setTesting] = useState({});
  const [checking, setChecking] = useState(false);
  const [newEmails, setNewEmails] = useState({});
  const [newNames, setNewNames] = useState({});

  const loadData = useCallback(async () => {
    try {
      setLoading(true);
      const [configsRes, logsRes] = await Promise.all([
        getAlertConfigs(),
        getAlertLogs({ limit: 30 }),
      ]);
      setConfigs(configsRes.data);
      setLogs(logsRes.data);
    } catch (err) {
      toast.error(t('alerts.errorLoading'));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadData(); }, [loadData]);

  const handleToggle = async (alertType, enabled) => {
    const config = configs.find(c => c.id === alertType);
    if (!config) return;

    setSaving(prev => ({ ...prev, [alertType]: true }));
    try {
      await updateAlertConfig(alertType, {
        recipients: config.recipients || [],
        enabled,
      });
      setConfigs(prev => prev.map(c => c.id === alertType ? { ...c, enabled } : c));
      toast.success(enabled ? t('alerts.activated') : t('alerts.deactivated'));
    } catch (err) {
      toast.error(t('alerts.errorUpdating'));
    } finally {
      setSaving(prev => ({ ...prev, [alertType]: false }));
    }
  };

  const handleAddRecipient = async (alertType) => {
    const email = newEmails[alertType]?.trim();
    const name = newNames[alertType]?.trim() || '';
    if (!email || !email.includes('@')) {
      toast.error(t('alerts.invalidEmail'));
      return;
    }

    const config = configs.find(c => c.id === alertType);
    if (!config) return;

    const recipients = [...(config.recipients || []), { email, name }];

    setSaving(prev => ({ ...prev, [alertType]: true }));
    try {
      await updateAlertConfig(alertType, {
        recipients,
        enabled: config.enabled,
      });
      setConfigs(prev => prev.map(c => c.id === alertType ? { ...c, recipients } : c));
      setNewEmails(prev => ({ ...prev, [alertType]: '' }));
      setNewNames(prev => ({ ...prev, [alertType]: '' }));
      toast.success(t('alerts.recipientAdded'));
    } catch (err) {
      toast.error(t('alerts.errorAddingRecipient'));
    } finally {
      setSaving(prev => ({ ...prev, [alertType]: false }));
    }
  };

  const handleRemoveRecipient = async (alertType, emailToRemove) => {
    const config = configs.find(c => c.id === alertType);
    if (!config) return;

    const recipients = (config.recipients || []).filter(r => r.email !== emailToRemove);

    setSaving(prev => ({ ...prev, [alertType]: true }));
    try {
      await updateAlertConfig(alertType, {
        recipients,
        enabled: config.enabled,
      });
      setConfigs(prev => prev.map(c => c.id === alertType ? { ...c, recipients } : c));
      toast.success(t('alerts.recipientRemoved'));
    } catch (err) {
      toast.error(t('alerts.errorRemovingRecipient'));
    } finally {
      setSaving(prev => ({ ...prev, [alertType]: false }));
    }
  };

  // Remove an email from every alert config it appears in (one click cleanup)
  const handleRemoveFromAll = async (emailToRemove) => {
    if (!window.confirm(`Eliminar ${emailToRemove} de TODAS las alertas?`)) return;
    try {
      const affected = configs.filter(c => (c.recipients || []).some(r => r.email === emailToRemove));
      await Promise.all(affected.map(cfg => {
        const recipients = (cfg.recipients || []).filter(r => r.email !== emailToRemove);
        return updateAlertConfig(cfg.id, { recipients, enabled: cfg.enabled });
      }));
      setConfigs(prev => prev.map(c => ({
        ...c,
        recipients: (c.recipients || []).filter(r => r.email !== emailToRemove),
      })));
      toast.success(`${emailToRemove} eliminado de ${affected.length} alerta(s)`);
    } catch (err) {
      toast.error('Error eliminando destinatario global');
    }
  };

  // Build {email -> {name, subscribedTo: Set<alert_id>}} for the global matrix
  const buildRecipientsMatrix = () => {
    const map = new Map();
    for (const cfg of configs) {
      for (const r of (cfg.recipients || [])) {
        if (!r.email) continue;
        const entry = map.get(r.email) || { email: r.email, name: r.name || '', subscribed: new Set() };
        if (!entry.name && r.name) entry.name = r.name;
        entry.subscribed.add(cfg.id);
        map.set(r.email, entry);
      }
    }
    return Array.from(map.values()).sort((a, b) => a.email.localeCompare(b.email));
  };

  const handleTestAlert = async (alertType) => {
    setTesting(prev => ({ ...prev, [alertType]: true }));
    try {
      await testAlert(alertType);
      toast.success(t('alerts.testSent'));
    } catch (err) {
      toast.error(err.response?.data?.detail || t('alerts.errorTesting'));
    } finally {
      setTesting(prev => ({ ...prev, [alertType]: false }));
    }
  };

  const handleCheckNow = async () => {
    setChecking(true);
    try {
      const res = await checkAlertsNow();
      const r = res.data.results;
      const total = (r.port_days?.alerts_sent || 0) + (r.arrival_days?.alerts_sent || 0);
      toast.success(t('alerts.checkCompleted'));
      loadData();
    } catch (err) {
      toast.error(t('alerts.errorSending'));
    } finally {
      setChecking(false);
    }
  };

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center h-64">
          <Loader2 className="animate-spin text-primary" size={32} />
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="space-y-6 max-w-4xl" data-testid="alerts-page">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-heading text-foreground flex items-center gap-2">
              <Bell className="text-primary" size={24} />
              {t('alerts.title')}
            </h1>
            <p className="text-muted-foreground mt-1">{t('alerts.subtitle')}</p>
          </div>
          <Button
            onClick={handleCheckNow}
            disabled={checking}
            data-testid="check-alerts-now-btn"
          >
            {checking ? <Loader2 size={16} className="mr-2 animate-spin" /> : <Play size={16} className="mr-2" />}
            {t('alerts.checkNow')}
          </Button>
        </div>

        {/* Global recipients matrix — see who receives what across all alert types */}
        {(() => {
          const matrix = buildRecipientsMatrix();
          if (matrix.length === 0) return null;
          return (
            <div className="bg-card border border-border rounded-lg overflow-hidden" data-testid="recipients-matrix">
              <div className="p-5 border-b border-border flex items-center gap-3">
                <div className="p-2 rounded-lg bg-muted text-primary">
                  <Mail size={20} />
                </div>
                <div>
                  <h3 className="font-heading text-lg text-foreground">Quién recibe qué</h3>
                  <p className="text-sm text-muted-foreground">
                    Vista global de destinatarios por tipo de alerta. Marca la casilla para suscribir / desuscribir.
                  </p>
                </div>
              </div>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="bg-muted/40">
                    <tr>
                      <th className="text-left p-3 font-medium text-muted-foreground">Destinatario</th>
                      {configs.map(cfg => {
                        const Icon = ALERT_ICONS[cfg.id] || Bell;
                        const colorClass = ALERT_COLORS[cfg.id] || 'text-blue-500';
                        return (
                          <th key={cfg.id} className="p-3 font-medium text-center" title={cfg.name}>
                            <div className={`flex flex-col items-center gap-1 ${colorClass}`}>
                              <Icon size={16} />
                              <span className="text-xs text-foreground/70 normal-case">{cfg.name?.split(' ')[0]}</span>
                            </div>
                          </th>
                        );
                      })}
                      <th className="p-3 w-12"></th>
                    </tr>
                  </thead>
                  <tbody>
                    {matrix.map((row) => (
                      <tr key={row.email} className="border-t border-border hover:bg-muted/20">
                        <td className="p-3">
                          <div className="font-medium text-foreground">{row.name || '(sin nombre)'}</div>
                          <div className="text-xs text-muted-foreground font-mono">{row.email}</div>
                        </td>
                        {configs.map(cfg => {
                          const subscribed = row.subscribed.has(cfg.id);
                          return (
                            <td key={cfg.id} className="p-3 text-center">
                              <button
                                type="button"
                                onClick={() => {
                                  if (subscribed) {
                                    handleRemoveRecipient(cfg.id, row.email);
                                  } else {
                                    const recipients = [...(cfg.recipients || []), { email: row.email, name: row.name }];
                                    setSaving(prev => ({ ...prev, [cfg.id]: true }));
                                    updateAlertConfig(cfg.id, { recipients, enabled: cfg.enabled })
                                      .then(() => {
                                        setConfigs(prev => prev.map(c => c.id === cfg.id ? { ...c, recipients } : c));
                                        toast.success(`${row.email} suscrito a ${cfg.name}`);
                                      })
                                      .catch(() => toast.error('Error al suscribir'))
                                      .finally(() => setSaving(prev => ({ ...prev, [cfg.id]: false })));
                                  }
                                }}
                                disabled={saving[cfg.id]}
                                className={`inline-flex items-center justify-center w-7 h-7 rounded-md transition-colors ${
                                  subscribed
                                    ? 'bg-primary/10 text-primary hover:bg-primary/20'
                                    : 'bg-muted/40 text-muted-foreground hover:bg-muted'
                                }`}
                                data-testid={`matrix-toggle-${row.email}-${cfg.id}`}
                                title={subscribed ? 'Desuscribir' : 'Suscribir'}
                              >
                                {subscribed ? <CheckCircle size={16} /> : <Plus size={14} />}
                              </button>
                            </td>
                          );
                        })}
                        <td className="p-3 text-center">
                          <button
                            type="button"
                            onClick={() => handleRemoveFromAll(row.email)}
                            className="inline-flex items-center justify-center w-7 h-7 rounded-md text-destructive hover:bg-destructive/10 transition-colors"
                            data-testid={`matrix-remove-all-${row.email}`}
                            title="Eliminar de TODAS las alertas"
                          >
                            <Trash2 size={14} />
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          );
        })()}

        {/* Alert Configs */}
        {configs.map((config) => {
          const Icon = ALERT_ICONS[config.id] || Bell;
          const colorClass = ALERT_COLORS[config.id] || 'text-blue-500';

          return (
            <div
              key={config.id}
              className="bg-card border border-border rounded-lg overflow-hidden"
              data-testid={`alert-config-${config.alert_type || config.id}`}
            >
              {/* Header */}
              <div className="p-5 flex items-center justify-between border-b border-border">
                <div className="flex items-center gap-3">
                  <div className={`p-2 rounded-lg bg-muted ${colorClass}`}>
                    <Icon size={22} />
                  </div>
                  <div>
                    <h3 className="font-heading text-lg text-foreground">{config.name}</h3>
                    <p className="text-sm text-muted-foreground">{config.description}</p>
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <Badge variant={config.enabled ? "default" : "secondary"}>
                    {config.threshold_days} dias
                  </Badge>
                  <Switch
                    checked={config.enabled}
                    onCheckedChange={(checked) => handleToggle(config.id, checked)}
                    disabled={saving[config.id]}
                    data-testid={`alert-toggle-${config.alert_type || config.id}`}
                  />
                </div>
              </div>

              {/* Recipients */}
              <div className="p-5">
                <h4 className="text-sm font-medium text-muted-foreground mb-3 flex items-center gap-2">
                  <Mail size={14} />
                  Destinatarios
                </h4>

                {/* Existing recipients */}
                <div className="space-y-2 mb-4">
                  {(config.recipients || []).length === 0 ? (
                    <p className="text-sm text-muted-foreground/60 italic">{t('alerts.noRecipientsConfigured')}</p>
                  ) : (
                    (config.recipients || []).map((r, i) => (
                      <div
                        key={i}
                        className="flex items-center justify-between bg-muted/50 rounded-lg px-4 py-2"
                        data-testid={`recipient-${config.id}-${i}`}
                      >
                        <div className="flex items-center gap-2">
                          <CheckCircle size={14} className="text-green-500" />
                          <span className="text-sm font-medium">{r.email}</span>
                          {r.name && <span className="text-xs text-muted-foreground">({r.name})</span>}
                        </div>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleRemoveRecipient(config.id, r.email)}
                          disabled={saving[config.id]}
                          data-testid={`remove-recipient-${config.id}-${i}`}
                        >
                          <Trash2 size={14} className="text-destructive" />
                        </Button>
                      </div>
                    ))
                  )}
                </div>

                {/* Add recipient form */}
                <div className="flex items-center gap-2">
                  <Input
                    placeholder={t('alerts.nameOptional')}
                    value={newNames[config.id] || ''}
                    onChange={(e) => setNewNames(prev => ({ ...prev, [config.id]: e.target.value }))}
                    className="w-36"
                    data-testid={`new-name-${config.id}`}
                  />
                  <Input
                    placeholder={t('alerts.emailPlaceholder')}
                    type="email"
                    value={newEmails[config.id] || ''}
                    onChange={(e) => setNewEmails(prev => ({ ...prev, [config.id]: e.target.value }))}
                    onKeyDown={(e) => e.key === 'Enter' && handleAddRecipient(config.id)}
                    className="flex-1"
                    data-testid={`new-email-${config.id}`}
                  />
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => handleAddRecipient(config.id)}
                    disabled={saving[config.id]}
                    data-testid={`add-recipient-${config.id}`}
                  >
                    <Plus size={14} className="mr-1" />
                    Agregar
                  </Button>
                </div>

                {/* Test button */}
                {config.recipients?.length > 0 && (
                  <div className="mt-4 pt-4 border-t border-border flex justify-end">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handleTestAlert(config.id)}
                      disabled={testing[config.id]}
                      data-testid={`test-alert-${config.id}`}
                    >
                      {testing[config.id] ? <Loader2 size={14} className="mr-2 animate-spin" /> : <Send size={14} className="mr-2" />}
                      {t('alerts.sendTestEmail')}
                    </Button>
                  </div>
                )}
              </div>
            </div>
          );
        })}

        {/* Alert History */}
        <div className="bg-card border border-border rounded-lg" data-testid="alert-logs">
          <div className="p-5 border-b border-border">
            <h3 className="font-heading text-lg text-foreground flex items-center gap-2">
              <AlertTriangle size={18} className="text-amber-500" />
              {t('alerts.alertHistory')}
            </h3>
          </div>
          
          {logs.length === 0 ? (
            <div className="p-8 text-center text-muted-foreground">
              <Bell size={32} className="mx-auto mb-2 opacity-30" />
              <p>{t('alerts.noAlertsSent')}</p>
            </div>
          ) : (
            <div className="divide-y divide-border max-h-96 overflow-y-auto">
              {logs.map((log, i) => {
                const Icon = ALERT_ICONS[log.alert_type] || Bell;
                const colorClass = ALERT_COLORS[log.alert_type] || 'text-blue-500';
                return (
                  <div key={i} className="p-4 flex items-center gap-4" data-testid={`alert-log-${i}`}>
                    <div className={`p-1.5 rounded ${colorClass} bg-muted`}>
                      <Icon size={16} />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <span className="font-medium text-sm">{log.container_number}</span>
                        <span className="text-xs text-muted-foreground">{log.shipment_mbl}</span>
                      </div>
                      <p className="text-xs text-muted-foreground">
                        {log.alert_type === 'port_days' ? `${log.days} ${t('alerts.daysInPortLog')}` : `${log.days} ${t('alerts.daysSinceArrivalLog')}`}
                        {' | '}
                        {t('alerts.sentTo')}: {(log.sent_to || []).join(', ')}
                      </p>
                    </div>
                    <div className="text-xs text-muted-foreground whitespace-nowrap">
                      {log.sent_at ? new Date(log.sent_at).toLocaleDateString('es-MX', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' }) : ''}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </DashboardLayout>
  );
}
